import Page from "components/Page/Page";

const PrivacyPolicy = () => {
	return (
		<Page title="Privacy Policy">
			<h1 className="text-2xl lg:text-[32px] font-bold text-dark mb-6">
				Privacy Policy
			</h1>

			<p className="text-sm text-dark mb-6">
				Last Updated: June 15, 2026
				<br />
				This Privacy Policy explains how The Accounter collects, uses, and
				protects your personal information when you use our website, mobile
				application, or accounting and bookkeeping services. By using our
				services, you consent to the practices described in this policy.
			</p>

			<h2 className="text-dark font-bold mb-6">SECTION 01 - WHO WE ARE</h2>

			<p className="text-sm text-dark mb-6">
				THE ACCOUNTER, trading as The Accounter, is a licensed accounting and
				bookkeeping firm registered in the United Arab Emirates. We provide
				accounting, bookkeeping, VAT filing, tax compliance, and financial
				reporting services to businesses and individuals across the UAE.
			</p>

			<p className="text-sm text-dark mb-6">
				Our registered office is at:
				<br />
				Fintech Hive, Zone D, Level 1 Gate Avenue, DIFC, Dubai, UAE.
			</p>

			<p className="text-sm text-dark mb-6">
				For data protection purposes, The Accounter is the data controller
				responsible for your personal information collected through our website
				at www.theaccounter.com and our mobile application.
			</p>

			<p className="text-sm text-dark mb-6">
				This policy applies to our operations on the UAE mainland. If you are
				based in or operate from the Dubai International Financial Centre (DIFC)
				or Abu Dhabi Global Market (ADGM), please note that those free zones
				operate under separate data protection frameworks.
			</p>

			<h2 className="text-dark font-bold mb-6">
				SECTION 02 - INFORMATION WE COLLECT
			</h2>

			<p className="text-sm text-dark mb-6">
				We collect personal information necessary to provide our services,
				communicate with you, and comply with our legal obligations.
			</p>

			<h3 className="text-dark font-bold mb-4">
				Information You Provide Directly
			</h3>

			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					Full name, email address, phone number, and business name when you
					register or contact us
				</li>
				<li>
					Trade licence details, company registration number, and Emirates ID
					(where required for compliance)
				</li>
				<li>
					Financial records, invoices, receipts, and transaction data you upload
					via our app or share with our team
				</li>
				<li>
					Bank account details where required to process payments or provide
					services
				</li>
				<li>
					Communications you send us via email, in-app messages, or contact
					forms
				</li>
			</ul>

			<h3 className="text-dark font-bold mb-4">
				Information Collected Automatically
			</h3>

			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					Device type, operating system, browser, and IP address when you use
					our website or app
				</li>
				<li>
					Pages visited, session duration, and interaction data for analytics
					and service improvement
				</li>
				<li>
					App usage data including features accessed and support queries raised
				</li>
			</ul>

			<h3 className="text-dark font-bold mb-4">
				Information From Third Parties
			</h3>

			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					Publicly available business registry information relevant to your
					account
				</li>
				<li>
					Payment processor confirmations when you subscribe to or pay for our
					services
				</li>
			</ul>

			<h2 className="text-dark font-bold mb-6">
				SECTION 03 - HOW WE USE YOUR DATA
			</h2>

			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					Delivering our accounting, bookkeeping, VAT filing, and financial
					reporting services
				</li>
				<li>
					Creating and managing your account, and verifying your identity where
					required
				</li>
				<li>
					Communicating with you about your account, service updates, and
					inquiries
				</li>
				<li>Processing payments and managing billing</li>
				<li>
					Complying with UAE tax, regulatory, and legal obligations including
					Federal Tax Authority requirements
				</li>
				<li>
					Detecting, preventing, and addressing fraud, errors, or security
					incidents
				</li>
				<li>
					Improving our platform, services, and customer experience through
					analytics
				</li>
				<li>
					Sending you relevant updates, service notifications, or promotional
					information (where consented or permitted by law)
				</li>
			</ul>

			<p className="text-sm text-dark mb-6">
				We will not use your data for purposes incompatible with those listed
				above without your prior consent.
			</p>

			<h2 className="text-dark font-bold mb-6">
				SECTION 04 - LEGAL BASIS FOR PROCESSING
			</h2>

			<p className="text-sm text-dark mb-6">
				We process your personal data under the following legal bases in
				accordance with UAE Federal Decree-Law No. 45 of 2021 on the Protection
				of Personal Data (PDPL):
			</p>

			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					<strong>Contractual necessity</strong> — to perform the services you
					have engaged us for
				</li>
				<li>
					<strong>Legal obligation</strong> — to comply with UAE laws including
					VAT law and FTA regulations
				</li>
				<li>
					<strong>Legitimate interests</strong> — to improve our services,
					prevent fraud, and communicate relevant updates
				</li>
				<li>
					<strong>Consent</strong> — for optional communications and marketing,
					which you may withdraw at any time
				</li>
			</ul>

			<h2 className="text-dark font-bold mb-6">
				SECTION 05 - DATA SHARING & THIRD PARTIES
			</h2>

			<p className="text-sm text-dark mb-6">
				We do not sell your personal data. We may share your information with
				trusted third parties only where necessary:
			</p>

			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>
					Service providers — Cloud storage, payment processors, and software
					platforms we use to deliver our services
				</li>
				<li>
					Regulatory authorities — The Federal Tax Authority (FTA), Ministry of
					Finance, or other UAE government bodies where required by law
				</li>
				<li>
					Professional advisors — Auditors, legal counsel, or compliance
					professionals bound by confidentiality obligations
				</li>
				<li>
					Business transfers — In the event of a merger, acquisition, or sale of
					assets, your data may be transferred as part of that transaction
				</li>
			</ul>

			<h2 className="text-dark font-bold mb-6">SECTION 06 - DATA RETENTION</h2>

			<p className="text-sm text-dark mb-4">
				We retain your personal data for as long as is necessary to fulfil the
				purposes for which it was collected, including:
			</p>

			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>For the duration of your active service subscription with us</li>
				<li>
					For a minimum of five (5) years after service termination, as required
					by UAE VAT law and the FTA Tax Procedures Law
				</li>
				<li>
					For a minimum of seven (7) years for records related to corporate tax,
					as required by UAE Corporate Tax Law
				</li>
				<li>
					For longer periods where required by law or where we have a legitimate
					interest
				</li>
			</ul>

			<p className="text-sm text-dark mb-6">
				When data is no longer required, we securely delete or anonymise it in
				accordance with our data lifecycle procedures.
			</p>

			<h2 className="text-dark font-bold mb-6">SECTION 07 - DATA SECURITY</h2>

			<p className="text-sm text-dark mb-4">
				We take the security of your data seriously. Our measures include:
			</p>

			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>Encrypted data transmission using SSL/TLS protocols</li>
				<li>Secure, access-controlled cloud infrastructure</li>
				<li>
					Role-based access controls limiting data access to authorised
					personnel only
				</li>
				<li>Regular security reviews and staff training on data handling</li>
			</ul>

			<p className="text-sm text-dark mb-6">
				While we implement industry-standard safeguards, no method of
				transmission over the internet or electronic storage is 100% secure. In
				the event of a personal data breach, we will notify the UAE Data Office
				within 72 hours of becoming aware of the breach, as required under the
				UAE PDPL.
			</p>

			<h2 className="text-dark font-bold mb-6">SECTION 08 - YOUR RIGHTS</h2>

			<p className="text-sm text-dark mb-4">
				Under the UAE Personal Data Protection Law (PDPL), you have the
				following rights regarding your personal data:
			</p>

			<ul className="list-disc list-inside text-sm text-dark mb-6 space-y-2">
				<li>Right to access</li>
				<li>Right to rectification</li>
				<li>Right to erasure</li>
				<li>Right to data portability</li>
				<li>Right to object</li>
				<li>Right to restriction</li>
				<li>Right to withdraw consent</li>
			</ul>

			<p className="text-sm text-dark mb-6">
				To exercise any of these rights, please contact us at{" "}
				<a
					href="mailto:hello@theaccounter.com"
					className="text-primary underline">
					hello@theaccounter.com
				</a>
				. We will respond within 30 days.
			</p>

			<h2 className="text-dark font-bold mb-6">
				SECTION 09 - INTERNATIONAL DATA TRANSFERS
			</h2>

			<p className="text-sm text-dark mb-6">
				Some of our service providers may store or process your data outside the
				UAE. Where this occurs, we take all reasonable steps to ensure your data
				receives equivalent protection to that required under UAE law, including
				through contractual safeguards with those providers.
			</p>

			<h2 className="text-dark font-bold mb-6">
				SECTION 10 - CHILDREN'S PRIVACY
			</h2>

			<p className="text-sm text-dark mb-6">
				Our services are intended for businesses and individuals aged 18 and
				over. We do not knowingly collect personal data from minors. If you
				believe we have inadvertently collected such data, please contact us and
				we will delete it promptly.
			</p>

			<h2 className="text-dark font-bold mb-6">
				SECTION 11 - CHANGES TO THIS POLICY
			</h2>

			<p className="text-sm text-dark mb-6">
				We may update this Privacy Policy from time to time to reflect changes
				in our practices, technology, or legal requirements. We will notify you
				of material changes by posting the updated policy on our website and,
				where appropriate, by email.
			</p>

			<h2 className="text-dark font-bold mb-6">SECTION 12 - CONTACT US</h2>

			<p className="text-sm text-dark mb-2">
				<strong>The Accounter</strong>
			</p>

			<p className="text-sm text-dark mb-2">
				Fintech Hive, Zone D, Level 1 Gate Avenue, DIFC, Dubai, UAE
			</p>

			<p className="text-sm text-dark mb-2">
				Email: customersupport@theaccounter.com
			</p>

			<p className="text-sm text-dark mb-6">
				Website:
				<span className="text-blue-500 underline">www.theaccounter.com</span>
			</p>
		</Page>
	);
};

export default PrivacyPolicy;
